import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ArticleListTable from "./ArticleListTable";
import ArticleListFilter from "./ArticleListFilter";
import ArticleListDeleteDialog from "./ArticleListDeleteDialog";

/** Types */
interface Article {
  id: string;
  title: string;
  date: string;
  category_id: string;
  topic: string;
  slug: string;
  publication_state: string;
  category?: {
    name: string;
  };
}

interface Category {
  id: string;
  name: string;
}

interface ArticleListProps {
  onEditArticle: (article: Article) => void;
}

export default function ArticleList({ onEditArticle }: ArticleListProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);
  const [stateFilter, setStateFilter] = useState<string>("all");

  useEffect(() => {
    fetchArticles();
    fetchCategories();
    // eslint-disable-next-line
  }, [stateFilter]);

  const fetchArticles = async () => {
    try {
      let query = supabase
        .from("articles")
        .select(`
          id, 
          title, 
          date, 
          category_id, 
          topic, 
          slug,
          publication_state
        `)
        .order("date", { ascending: false });
      
      if (stateFilter !== "all") {
        query = query.eq("publication_state", stateFilter);
      }
      const { data, error } = await query;

      if (error) {
        console.error("Error fetching articles:", error);
        toast.error("Failed to load articles");
        setArticles([]);
      } else {
        setArticles(data as Article[] || []);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Failed to load articles");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name");
      if (error) throw error;
      const categoryMap: Record<string, string> = {};
      (data || []).forEach((category: Category) => {
        categoryMap[category.id] = category.name;
      });
      setCategories(categoryMap);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleDeleteClick = (article: Article) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!articleToDelete) return;
    try {
      const { error } = await supabase
        .from("articles")
        .delete()
        .eq("id", articleToDelete.id);
      if (error) throw error;
      setArticles(articles.filter(article => article.id !== articleToDelete.id));
      toast.success("Article deleted successfully");
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Failed to delete article");
    } finally {
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-white">Loading articles...</div>;
  }

  return (
    <>
      <div className="mb-4 flex justify-end">
        <ArticleListFilter value={stateFilter} onChange={setStateFilter} />
      </div>
      <ArticleListTable
        articles={articles}
        categories={categories}
        onEdit={onEditArticle}
        onDelete={handleDeleteClick}
      />
      <ArticleListDeleteDialog
        open={deleteDialogOpen}
        article={articleToDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
// NOTE TO USER: This file just got a lot shorter and easier to maintain! 🎉
